import type { CSSProperties } from "react";

/**
 * Mantine's own dark scheme, scoped to one part of the page (the floorplan
 * toolbar over the canvas), whatever the app's scheme is (#472). The
 * root-level colour variables the provider sets for `dark` are set on the
 * element, with `data-mantine-color-scheme="dark"` so Mantine's component
 * rules for dark (hover, borders) apply inside it. A combobox's dropdown
 * renders in a portal, outside that element, so it gets the same pair.
 */
export const DARK_SCHEME_VARS = {
  "--mantine-color-scheme": "dark",
  "--mantine-color-bright": "var(--mantine-color-white)",
  "--mantine-color-text": "var(--mantine-color-dark-0)",
  "--mantine-color-body": "var(--mantine-color-dark-7)",
  "--mantine-color-error": "var(--mantine-color-red-8)",
  "--mantine-color-placeholder": "var(--mantine-color-dark-3)",
  "--mantine-color-default": "var(--mantine-color-dark-6)",
  "--mantine-color-default-hover": "var(--mantine-color-dark-5)",
  "--mantine-color-default-color": "var(--mantine-color-white)",
  "--mantine-color-default-border": "var(--mantine-color-dark-4)",
  "--mantine-color-dimmed": "var(--mantine-color-dark-2)",
  "--mantine-color-disabled": "var(--mantine-color-dark-6)",
  "--mantine-color-disabled-color": "var(--mantine-color-dark-3)",
  "--mantine-color-disabled-border": "var(--mantine-color-dark-4)",
  "--popover-border-color": "var(--mantine-color-dark-4)",
  color: "var(--mantine-color-dark-0)",
} as CSSProperties;

const DARK_ATTRIBUTE = { "data-mantine-color-scheme": "dark" };

/** Spread on a Mantine element to give it and everything inside it the
 *  theme's dark scheme (the floorplan toolbar: its buttons, its fields). */
export const darkSchemeScope = { ...DARK_ATTRIBUTE, style: DARK_SCHEME_VARS } as const;

/** The portalled dropdown: Mantine paints a popover's background from its
 *  *ancestor's* scheme (`:where([data-mantine-color-scheme]) .popover`), and
 *  in the portal that ancestor is the app's, so the dark one is set here. */
const DARK_DROPDOWN = { ...DARK_SCHEME_VARS, backgroundColor: "var(--mantine-color-dark-6)" } as CSSProperties;

/** `attributes` + `styles` for a Mantine combobox field (`Select`,
 *  `MultiSelect`) in the dark scheme, its portalled dropdown included. */
export const darkComboboxProps = {
  attributes: { root: DARK_ATTRIBUTE, dropdown: DARK_ATTRIBUTE },
  styles: { root: DARK_SCHEME_VARS, dropdown: DARK_DROPDOWN },
} as const;
